import { makeStyles, createStyles, Grid, Theme, Typography } from "@material-ui/core";
import { Map, Room, Search } from "@material-ui/icons";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { ILatLng, selectIsMobile } from "../../redux/slices/cleverlynkSlice";
import { LupapCityCodes, QueryState, selectDeliveryInfo } from "../../redux/slices/mapServiceSlice";
import ConfirmAddressMap from "../DeliveryMapService/ConfirmAddressMap";
import { IActualMapProvider, IMapProvider } from "../DeliveryMapService/DeliveryInput";
import DynamicAddress from "../DeliveryMapService/DynamicAddress";
import DynamicMap from "../DeliveryMapService/DynamicMap";
import FirstTimeQueryMap from "./FirstTimeQueryMap";

import { useTranslation } from "react-i18next";
import "../../i18n/config";

enum FirstTimeQueryStage {
  "MAP",
  "ADDRESS",
  "CONFIRM",
}

interface IFirstTimeQuery {
  onConfirm: (location: ILatLng, dInfo?: Partial<QueryState>) => void;
  toggleFocus: () => void;
}

const FirstTimeQuery = ({ onConfirm, toggleFocus }: IFirstTimeQuery) => {
  const isMobile = useSelector(selectIsMobile);

  const [stage, setStage] = useState<number>(FirstTimeQueryStage.ADDRESS);
  const [partialInfo, setPartialInfo] = useState<Partial<QueryState>>({});

  const { t } = useTranslation("deliveryinput");

  const deliveryInfo = useSelector(selectDeliveryInfo);

  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      togglebuttongroup: {
        transition: "padding 200ms",
      },
      togglebutton: {
        backgroundColor: "white",
        "&.Mui-selected": {
          backgroundColor: "#b3f7e3 !important",
        },
      },
    })
  );

  const classes = useStyles();

  const queryLocation = useMemo(() => {
    return { city: deliveryInfo.city ?? "Bogotá", countryCode: deliveryInfo.country ?? "co" };
  }, [deliveryInfo]);

  const mapProvider: IActualMapProvider = useMemo(() => {
    const coordinates = LupapCityCodes[queryLocation.city] ? IMapProvider.LUPAP : IMapProvider.AWS;
    const autocomplete = IMapProvider.AWS;
    return { coordinates, autocomplete };
  }, [queryLocation]);

  const content = useMemo(() => {
    switch (stage) {
      case FirstTimeQueryStage.ADDRESS:
        return (
          <DynamicAddress
            toggleMap={() => setStage(FirstTimeQueryStage.MAP)}
            toggleSelected={a => {
              setPartialInfo(a!);
              setStage(FirstTimeQueryStage.CONFIRM);
            }}
            city={queryLocation.city}
            countryCode={queryLocation.countryCode}
            mapProvider={mapProvider}
            toggleFocus={toggleFocus}
            citySelector
          />
        );
      case FirstTimeQueryStage.CONFIRM:
        return (
          <ConfirmAddressMap
            toggleAddress={() => setStage(FirstTimeQueryStage.ADDRESS)}
            toggleSelected={(location: ILatLng) => onConfirm(location, { ...partialInfo })}
            bannerPosition={"TOP"}
          />
        );
      case FirstTimeQueryStage.MAP:
        toggleFocus();
        return <FirstTimeQueryMap mapProvider={mapProvider.coordinates} toggleSelected={onConfirm} />;
      default:
        return null;
    }
  }, [mapProvider, onConfirm, partialInfo, queryLocation.city, queryLocation.countryCode, stage, toggleFocus]);

  const subtitle = useMemo(() => {
    switch (stage) {
      case FirstTimeQueryStage.ADDRESS:
        return t("firstTimeQuery.addressSubtitle");
      case FirstTimeQueryStage.CONFIRM:
        return t("firstTimeQuery.confirmSubtitle");
      case FirstTimeQueryStage.MAP:
        return t("firstTimeQuery.mapSubtitle");
      default:
        break;
    }
  }, [stage]);

  return (
    <Grid container direction="column" style={{ marginTop: 5 }}>
      <Grid item container justifyContent="space-between" wrap="nowrap">
        <Grid
          item
          xs={10}
          container
          alignItems="center"
          style={{ margin: stage === FirstTimeQueryStage.CONFIRM ? "10px 0px" : "" }}>
          <Typography style={{ color: "#717171", fontSize: isMobile ? "0.85rem" : "1rem" }}>{subtitle}</Typography>
        </Grid>
        <Grid item className={`${stage === FirstTimeQueryStage.MAP ? "map" : "list"} ${classes.togglebuttongroup}`}>
          {stage !== FirstTimeQueryStage.CONFIRM && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={stage}
              onChange={(_e, v) => (v !== null ? setStage(v) : null)}>
              <ToggleButton className={classes.togglebutton} value={FirstTimeQueryStage.MAP}>
                <Room />
              </ToggleButton>
              <ToggleButton className={classes.togglebutton} value={FirstTimeQueryStage.ADDRESS}>
                <Search />
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </Grid>
      </Grid>
      {content}
    </Grid>
  );
};
export default FirstTimeQuery;
